/**
 * MetricGamePlanModal — the "full cycle" for a benchmarked metric.
 * ================================================================
 * Synopsis + standing vs industry benchmark → trusted SourceBadge → what we're
 * seeing → a tailored roadmap (AI-enriched, deterministic fallback) → persist a
 * tracked action plan and assign each step to a staff member.
 *
 * Backed by /api/insight-actions: generate-plan, plans, plans/{id}/steps/{id},
 * assignable-staff. All numbers sent for AI enrichment are aggregate (non-PII)
 * and routed through the PII-masking firewall server-side.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Target, TrendingUp, TrendingDown, CheckCircle2, Sparkles, FileText,
  UserPlus, Loader2, Eye, ListChecks,
} from 'lucide-react';
import { toast } from '@/utils/safeToast';
import SourceBadge from '@/components/common/SourceBadge';
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';

// Legacy short source keys → benchmark-source registry ids used by SourceBadge.
const SOURCE_ID_MAP = {
  ato: 'ato_benchmarks', abs: 'abs_8165', rba: 'rba_f11', ibisworld: 'corvana_customer_aggregate',
  corvana_aggregate: 'corvana_customer_aggregate', corvana_ai: 'corvana_customer_aggregate',
  restaurant_catering: 'restaurant_catering_au', ara: 'ara_retail', hia: 'hia_outlook',
  ahpra: 'ahpra', ava: 'ava', medicare: 'medicare_mbs', fitness_australia: 'ausactive',
};
const resolveSourceId = (id) => SOURCE_ID_MAP[id] || id || 'ato_benchmarks';

const STANDING = {
  ahead: { label: 'Ahead of benchmark', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: TrendingUp },
  on_track: { label: 'In line with benchmark', cls: 'bg-blue-100 text-blue-700 border-blue-200', Icon: CheckCircle2 },
  behind: { label: 'Below benchmark', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: TrendingDown },
  critical: { label: 'Well below benchmark', cls: 'bg-red-100 text-red-700 border-red-200', Icon: TrendingDown },
};
const EFFORT = { Low: 'text-emerald-600 border-emerald-200', Medium: 'text-blue-600 border-blue-200', High: 'text-amber-600 border-amber-200' };

export default function MetricGamePlanModal({ open, onClose, metric }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedPlan, setSavedPlan] = useState(null);
  const [staff, setStaff] = useState([]);

  const m = metric || {};
  const unit = m.unit ?? '';

  const load = useCallback(async () => {
    if (!metric) return;
    setLoading(true); setSavedPlan(null);
    try {
      const r = await authFetch(`${API_URL}/api/insight-actions/generate-plan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metric: m.label, your_value: Number(m.value) || 0, benchmark: Number(m.benchmark) || 0,
          unit, good_direction: m.goodDirection || 'higher', industry: m.industry || 'general',
          source_note: m.sourceNote || '',
        }),
      });
      setPlan(r.ok ? await r.json() : null);
    } catch { setPlan(null); }
    setLoading(false);
  }, [metric]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const createPlan = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API_URL}/api/insight-actions/plans`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          insight_title: `Improve ${m.label}`,
          insight_type: plan.standing === 'ahead' ? 'opportunity' : 'risk',
          industry: m.industry || 'general',
          business_id: m.businessId || '', business_name: m.businessName || '',
          expected_impact: plan.expected_outcome || '',
          deep_analysis: `${plan.synopsis}\n\n${plan.what_we_see}`,
          data_sources: [m.sourceNote || 'Industry benchmark'],
          methodology: 'Benchmark gap analysis',
          steps: plan.steps || [],
          baseline_metrics: [{ label: m.label, value: `${m.value}${unit}` }],
        }),
      });
      if (r.ok) {
        const saved = await r.json();
        setSavedPlan(saved);
        toast.success('Action plan added to your roadmap');
        const sr = await authFetch(`${API_URL}/api/insight-actions/assignable-staff`);
        if (sr.ok) setStaff((await sr.json()).staff || []);
      } else { toast.error('Could not create action plan'); }
    } catch { toast.error('Could not create action plan'); }
    setSaving(false);
  };

  const assignStep = async (stepId, email) => {
    const person = staff.find((s) => s.email === email);
    try {
      const r = await authFetch(`${API_URL}/api/insight-actions/plans/${savedPlan.plan_id}/steps/${stepId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: email, assigned_to_name: person?.name || email }),
      });
      if (r.ok) { setSavedPlan(await r.json()); toast.success(`Assigned to ${person?.name || email}`); }
    } catch { toast.error('Assignment failed'); }
  };

  const standing = STANDING[plan?.standing] || STANDING.on_track;
  const StandingIcon = standing.Icon;
  const steps = savedPlan?.steps || plan?.steps || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="metric-gameplan-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <Target className="w-6 h-6 text-indigo-600" />
            {m.label || 'Metric'} — Game Plan
          </DialogTitle>
          <DialogDescription className="sr-only">
            How your {m.label || 'metric'} compares to the industry benchmark, with a recommended action plan you can track and assign.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-3 text-slate-500 py-12 justify-center" data-testid="gameplan-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Building your tailored game plan…
          </div>
        ) : !plan ? (
          <div className="py-12 text-center text-sm text-slate-500">Couldn’t build a plan right now. Please try again.</div>
        ) : (
          <div className="space-y-5 mt-3">
            {/* Synopsis & standing */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="gameplan-synopsis">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-slate-900">{m.value}{unit}</span>
                  <span className="text-xs text-slate-500">your business</span>
                  <span className="text-slate-300">vs</span>
                  <span className="text-lg font-semibold text-emerald-700">{m.benchmark}{unit}</span>
                  <span className="text-xs text-slate-500">industry avg</span>
                </div>
                <Badge className={`text-[11px] border ${standing.cls}`} data-testid="gameplan-standing">
                  <StandingIcon className="w-3 h-3 mr-1" />
                  {standing.label}{plan.variance_pct ? ` · ${Math.abs(plan.variance_pct)}%` : ''}
                </Badge>
              </div>
              <p className="text-sm text-slate-800 font-medium">{plan.synopsis}</p>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-[11px] text-slate-500">Benchmark source:</span>
                <SourceBadge sourceId={resolveSourceId(m.sourceId)} />
              </div>
            </div>

            {/* What we're seeing */}
            <div data-testid="gameplan-what-we-see">
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-1.5">
                <Eye className="w-4 h-4 text-indigo-600" /> What we’re seeing
              </h4>
              <p className="text-sm text-slate-600 leading-relaxed">{plan.what_we_see}</p>
            </div>

            {/* Roadmap */}
            <div data-testid="gameplan-roadmap">
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-2">
                <ListChecks className="w-4 h-4 text-indigo-600" /> Recommended roadmap
                {plan.ai_generated && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-indigo-500"><Sparkles className="w-3 h-3" /> AI-tailored</span>
                )}
              </h4>
              <div className="space-y-2">
                {steps.map((s, i) => (
                  <div key={s.step_id || i} className="rounded-lg border border-slate-200 p-3" data-testid={`gameplan-step-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="text-sm font-medium text-slate-800">{s.step || s.title}</p>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className={`text-[9px] ${EFFORT[s.effort] || EFFORT.Medium}`}>{s.effort || 'Medium'}</Badge>
                            <span className="text-[10px] text-slate-400">{s.timeline}</span>
                          </div>
                        </div>
                        {s.detail && <p className="text-xs text-slate-500 mt-0.5">{s.detail}</p>}
                        {/* Staff assignment (after the plan is tracked) */}
                        {savedPlan && (
                          <div className="flex items-center gap-2 mt-2">
                            <UserPlus className="w-3.5 h-3.5 text-slate-400" />
                            <select
                              data-testid={`gameplan-assign-${i}`}
                              value={s.assigned_to || ''}
                              onChange={(e) => assignStep(s.step_id, e.target.value)}
                              className="text-[11px] px-2 py-1 rounded-md border border-slate-200 bg-white text-slate-700"
                            >
                              <option value="">Assign to…</option>
                              {staff.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
                            </select>
                            {s.assigned_to_name && <span className="text-[10px] text-emerald-600">✓ {s.assigned_to_name}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Expected outcome */}
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="gameplan-outcome">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-emerald-800">Expected outcome</h4>
                <p className="text-sm text-emerald-700 mt-0.5">{plan.expected_outcome}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={onClose} data-testid="gameplan-close">Close</Button>
              {!savedPlan ? (
                <Button onClick={createPlan} disabled={saving} data-testid="gameplan-create" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Add to roadmap & assign
                </Button>
              ) : (
                <a
                  href="/app/action-plans"
                  className="inline-flex items-center gap-1.5 text-sm text-emerald-600 px-3 hover:underline"
                  data-testid="gameplan-saved"
                >
                  <CheckCircle2 className="w-4 h-4" /> Tracked in your roadmap — view
                </a>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
